import { useEffect, useRef } from 'react';
const Konva = require('konva');
import { scaleText, scaleImage } from '../services/ScaleService';
import { cleanProductImageGroup } from '../services/ProductHelperService';
import { applyAutocropToNode } from '../services/autocropService';
import { fetchAutocropBbox } from '../../../services/appImagesApi';
import { findImageBackgroundRect } from '../utils/findImageBackgroundRect';
import usePropertiesStore from '../stores/usePropertiesStore';
import useProductStore from '../stores/useProductStore';
import { refreshTransform } from '../components/properties/PropertiesHelper';
import { setProductSyncAbort } from '../services/productSyncController';

const FEED_APPLIED_URL_ATTR = '__feedAppliedUrl';


export function useGraphicEditorProductSync({
  product,
  stage,
  project,
  routeProjectIdNum,
  setElementVisibility,
  attachedOpsRef,
}) {
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!product || !stage) return;
    if (!Number.isFinite(routeProjectIdNum) || Number(project?.id) !== routeProjectIdNum) return;
    if (useProductStore.getState().isSaving) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    setProductSyncAbort(() => controller.abort());

    const promises = [];
    const ao = attachedOpsRef.current;
    const changedTextGroups = new Set();
    const changedImageGroups = new Set();
    const textsList = stage.find('Text');
    const imageGroups = stage.find('.elementGroup').filter((g) => g?.attrs?.elementType === 'image');
    for (const imageGroup of imageGroups) {
      if (imageGroup?.children.length > 2) {
        const cleaned = cleanProductImageGroup(imageGroup.children);
        // cleanProductImageGroup keeps at most {background, dynamic image}.
        // If both not present, skip cleanup — destroying then re-adding
        // a partial set would crash on Konva.add(undefined).
        if (cleaned.length < 2) continue;
        imageGroup.destroyChildren();
        imageGroup.add(...cleaned);
      }
    }
    const imagesList = stage.find('Image');

    textsList.forEach((text) => {
      if (!text?.getAttr?.('dynamicField')) return;
      const textGroup = text.parent;
      if (!textGroup) return;
      const textValue = product[text.getAttr('dynamicField')] || text.getAttr('placeholder');
      text.text(textValue);
      text.setAttr('fullText', textValue);
      if (!textValue) {
        setElementVisibility(textGroup, false);
        return;
      }
      setElementVisibility(textGroup, true);
      if (textGroup?.getAttr?.('mode') === 'flex') {
        textGroup.setAttr('extraDimension', null);
      }
      promises.push(
        new Promise((res, rej) => {
          scaleText(
            text,
            () => {
              if (textGroup?.getStage?.()) changedTextGroups.add(textGroup);
              res();
            },
            rej,
          );
        }),
      );
    });

    const redrawCanvas = () => {
      if (signal.aborted || !stage?.getStage?.()) return;
      const canvasLayer = stage.findOne('#canvas');
      canvasLayer?.draw();
      stage.draw();
    };

    imagesList.forEach((image) => {
      if (!image?.getAttr?.('dynamicField')) return;

      const group = image.parent;
      if (!group) return;
      const rect = findImageBackgroundRect(group);
      const imgAttrs = image.getAttrs();
      const field = imgAttrs.dynamicField;
      const nextUrl = product[field];

      if (!nextUrl) {
        if (image.image()) {
          scaleImage(image);
          if (group?.getStage?.()) changedImageGroups.add(group);
          redrawCanvas();
        }
        return;
      }

      if (image.getAttr(FEED_APPLIED_URL_ATTR) === nextUrl && image.image()) {
        scaleImage(image);
        if (group?.getStage?.()) changedImageGroups.add(group);
        redrawCanvas();
        promises.push(Promise.resolve());
        return;
      }

      const imageObj = new window.Image();
      promises.push(
        new Promise((res, rej) => {
          imageObj.onload = async function () {
            if (signal.aborted) return res();
            try {
              const oldImage = imgAttrs.image;
              delete imgAttrs.image;
              delete imgAttrs.width;
              delete imgAttrs.height;
              delete imgAttrs.crop;
              delete imgAttrs.cropX;
              delete imgAttrs.cropY;
              delete imgAttrs.cropWidth;
              delete imgAttrs.cropHeight;

              const bgRect = rect || findImageBackgroundRect(group);
              if (!bgRect) {
                console.warn('GraphicEditor product sync: missing background rect for field', field);
              }

              const imgNode = new Konva.Image({
                ...imgAttrs,
                image: imageObj,
                url: nextUrl,
                initWidth: imageObj.naturalWidth || imageObj.width,
                initHeight: imageObj.naturalHeight || imageObj.height,
                mode: image?.getAttr?.('mode'),
              });
              imgNode.setAttr(FEED_APPLIED_URL_ATTR, nextUrl);
              if (bgRect) bgRect.fillPatternImage(imageObj);

              if (image.getAttr('cropBackground')) {
                try {
                  const bboxResp = await fetchAutocropBbox(
                    nextUrl,
                    image.getAttr('useFirstPixel'),
                  );
                  if (signal.aborted) return res();
                  await applyAutocropToNode(imgNode, bgRect, bboxResp);
                } catch (cropErr) {
                  console.warn(
                    'GraphicEditor product sync: autocrop failed, using full image',
                    field,
                    String(cropErr?.message || cropErr),
                  );
                }
              }

              image.destroy();
              group.add(imgNode);
              if (oldImage) oldImage.remove();
              scaleImage(imgNode);
              if (group?.getStage?.()) changedImageGroups.add(group);
              redrawCanvas();
              res();
            } catch (err) {
              console.warn('GraphicEditor product sync: dynamic image replace failed', field, err);
              rej(err);
            }
          };
          imageObj.onerror = () => {
            console.warn('GraphicEditor product sync: failed to load image URL for field', field);
            res();
          };
          imageObj.src = nextUrl;
        }),
      );
    });

    const checkCondition = usePropertiesStore.getState().checkCondition;
    if (!stage?.getStage?.()) return () => controller.abort();

    stage.find('.condition').forEach((el) => {
      if (el?.getStage?.()) checkCondition(el);
    });
    const distributedChildren = [];
    const distributedGroups = stage.find('.distributed').filter((g) => g?.getStage?.());
    distributedGroups.forEach((g) => distributedChildren.push(...(g?.children || [])));
    stage.draw();
    if (ao?.reDistributeGroup) {
      distributedGroups.forEach((el) => {
        if (el?.getStage?.()) ao.reDistributeGroup(el);
      });
    }

    Promise.all(promises)
      .then(async () => {
        if (signal.aborted || !stage?.getStage?.()) return;
        if (!ao?.moveAttachedSources) return;
        for (const group of changedTextGroups) {
          if (signal.aborted) return;
          if (!group?.getStage?.()) continue;
          refreshTransform(group);
          if (group?.getAttr?.('attachIds')?.length) {
            await ao.moveAttachedSources(group);
          }
        }
        for (const group of changedImageGroups) {
          if (signal.aborted) return;
          if (!group?.getStage?.()) continue;
          refreshTransform(group);
          if (group?.getAttr?.('attachIds')?.length) {
            await ao.moveAttachedSources(group);
          }
        }
        stage.find('.target').forEach((el) => {
          if (signal.aborted) return;
          if (!el?.getStage?.()) return;
          if (!el.hasName('source') && !distributedChildren.includes(el)) {
            ao.moveAttachedSources(el);
          }
        });
      })
      .finally(() => {
        if (signal.aborted) return;
        redrawCanvas();
      });

    return () => controller.abort();
  }, [product, stage, project, routeProjectIdNum, setElementVisibility, attachedOpsRef]);
}
